/**
 * errorReporter — POSTs frontend failures to the backend's
 * `/api/incidents/report` endpoint so support@japantollreceipts.jp gets
 * an early-warning email whenever the site starts erroring on real
 * users, BEFORE customers email in to complain.
 *
 * Debouncing:
 *   - Same signature (classification + endpoint + status) → only one
 *     report per page session (kept in `sessionStorage`).
 *   - All POSTs are best-effort — if the reporting endpoint itself
 *     fails (full outage), we swallow the error so we don't loop.
 *
 * Privacy:
 *   - We send only: status code, classification, endpoint path,
 *     short browser message, user-agent, and the user's email if
 *     they're logged in. No request bodies, no auth tokens, no
 *     replay payloads.
 */
const API = process.env.REACT_APP_BACKEND_URL;

const SESSION_KEY = "jtr_reported_incidents";

function loadReportedSet() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch (_e) {
    return new Set();
  }
}

function saveReportedSet(set) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...set]));
  } catch (_e) { /* noop */ }
}

/**
 * Report a classified incident to the backend. Idempotent per
 * (classification + endpoint + status) within a single browser tab
 * session.
 */
export async function reportIncident({
  classification,
  status,
  endpoint,
  message,
  userEmail,
}) {
  if (!API) return;
  const sig = `${classification}|${status || 0}|${endpoint || ""}`;
  const reported = loadReportedSet();
  if (reported.has(sig)) return; // already reported in this tab session
  reported.add(sig);
  saveReportedSet(reported);

  const payload = {
    classification: String(classification || "unknown").slice(0, 64),
    status_code: typeof status === "number" ? status : null,
    endpoint: String(endpoint || "").slice(0, 256),
    message: String(message || "").slice(0, 512),
    user_agent: (navigator.userAgent || "").slice(0, 256),
    user_email: userEmail ? String(userEmail).slice(0, 128) : null,
    occurred_at: new Date().toISOString(),
  };

  try {
    // Fire-and-forget. Don't await long — if the backend is fully
    // down, this will fail too, and that's fine.
    await fetch(`${API}/api/incidents/report`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_e) {
    // Reporter is best-effort — never loop.
  }
}
